// ─── Cuánto vale lo que ENTREGA una remisión ────────────────────────────────
// Una remisión es un documento de ENTREGA: sus líneas van sin precio a
// propósito (quien recibe la mercancía no tiene por qué ver cifras). Pero el
// control de remisiones sin facturar y el informe de ingresos en riesgo sí
// necesitan saber cuánta plata está pendiente de facturar, y ese valor sale de
// la LÍNEA de la orden de origen, no del total de la orden entera.
//
// Por qué vive en core: lo usan el módulo de documentos (el panel de la lista
// de remisiones) y el de informes, y antes cada uno lo calculaba a su manera.
// Dos fuentes de verdad para la misma cifra terminan discrepando, y discreparon:
//
//   OP-0209 = $8.910.720 (400 libretas + 400 sets de escritorio + 1.000 sets
//   de memos) se entregó en DOS remisiones parciales:
//     · RS-0219 → 1.000 sets de memos                      = $2.853.620
//     · RS-0222 → 400 libretas + 400 sets de escritorio     = $6.057.100
//   El panel devolvía el TOTAL DE LA ORDEN para cada una: $8.910.720 en las
//   dos, y "$17.821.440 por facturar" — el doble de lo que de verdad había
//   pendiente. El informe, que sí sumaba línea por línea, decía otra cosa.
import { DOC_INFO } from "./config.js";
import { desgloseIVA } from "./iva.js";
import { state } from "./state.js";
import { normalize, parseNumber } from "./utils.js";

// El documento del que nació la remisión (normalmente una OP), que es donde
// viven los precios acordados con el cliente.
export function origenDeRemision(rc) {
  const tipo = rc && rc._origen && rc._origen.tipo;
  const info = tipo ? DOC_INFO[tipo] : null;
  if (!info) return null;
  return (state.data[info.key] || []).find(d => d.id === rc._origen.id) || null;
}

// Las líneas de la remisión con el precio de la orden de origen puesto. El
// régimen de IVA se arrastra de la línea de origen cuando lo lleva; si no,
// `desgloseIVA` lo resuelve por el producto (ver core/iva.js), que es la única
// puerta que decide la tarifa de una línea.
export function lineasValoradasRemision(rc) {
  const origen = origenDeRemision(rc);
  const itemsOrigen = origen ? (origen.items || []) : [];
  const lineas = (rc && rc.items || []).map(it => {
    const cantidad = parseNumber(it.cantidad) || 0;
    const enOrigen = itemsOrigen.find(x => normalize(x.articulo || "") === normalize(it.articulo || ""));
    // El precio propio de la línea manda si lo tiene (una remisión suelta, sin
    // orden detrás, puede llevarlo); si no, el de la línea de la orden.
    const precio = (parseNumber(it.precio_unitario) || 0)
      || (enOrigen ? parseNumber(enOrigen.precio_unitario) || 0 : 0);
    const linea = {
      articulo: it.articulo, cantidad, precio_unitario: precio, total: cantidad * precio
    };
    const tipo = it.iva_tipo || (enOrigen && enOrigen.iva_tipo);
    if (tipo) {
      linea.iva_tipo = tipo;
      const pct = it.iva_pct != null ? it.iva_pct : (enOrigen ? enOrigen.iva_pct : null);
      if (pct != null) linea.iva_pct = pct;
    }
    return linea;
  });
  return { origen, lineas };
}

// El valor por facturar de una remisión, con su IVA sumado línea por línea
// (un bien excluido del art. 424 no causa impuesto ni aquí).
export function valorRemision(rc) {
  const { origen, lineas } = lineasValoradasRemision(rc);
  // Sin NADA que entregar —ni líneas, o todas en cero— no hay nada por
  // facturar. El respaldo de abajo no aplica aquí: valorar un borrador vacío
  // por el total de su orden metía en el pendiente una entrega que no existe
  // (RS-0223, vacía, habría pesado los $10.906.112 de la OP-0214).
  const conCantidad = lineas.filter(l => l.cantidad > 0);
  if (!conCantidad.length) return 0;
  const valoradas = conCantidad.filter(l => l.total > 0);
  // Tiene líneas pero ninguna se pudo valorar —artículos renombrados después de
  // la entrega, o una orden que ya no está—: el total de la orden es una
  // estimación pobre, pero mejor que un cero que esconde plata por facturar.
  if (!valoradas.length) return origen ? parseNumber(origen.total) || 0 : 0;
  return desgloseIVA(valoradas).total;
}
